"use client";

import { EpisodeSummaryModel, SeriesModel, SeriesAccessModel } from "@api/models";
import EpisodeListItem from "./EpisodeListItem";
import { useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { getWaitAndFreeWaiting, seriesAccessToepisodeAccessSet } from "@lib/utils";
import { EpisodeDisplayedOn } from "../../../types/types";

const loadEps = (
  seriesHash: string,
  from: number,
  num: number,
): Promise<{ series: SeriesModel }> => {
  return fetch(
    `/api/episodes?seriesHash=${seriesHash}&episodeTo=${from + num}&episodeFrom=${from}`,
  ).then((res) => res.json());
};

const loadAccess = (
  seriesHash: string,
  from: number,
  num: number,
): Promise<{ seriesAccess: SeriesAccessModel }> => {
  return fetch(
    `/api/series/access?seriesHash=${seriesHash}&episodeTo=${from + num}&episodeFrom=${from}`,
  ).then((res) => res.json());
};

type SeriesEpisodeListLoaderParams = {
  seriesHash: string;
  seriesWaitOn: number | undefined;
  start: number;
  end: number;
  sort?: "new" | "old";
  currentEpisodeId?: string;
  displayedOn: EpisodeDisplayedOn;
};

export function SeriesEpisodeListLoader({
  seriesHash,
  seriesWaitOn,
  start,
  end,
  sort,
  currentEpisodeId,
  displayedOn,
}: SeriesEpisodeListLoaderParams) {
  const [loadedEpisodes, setLoadedEpisodes] = useState<EpisodeSummaryModel[]>([]);
  const [loadedAccess, setLoadedAccess] = useState<SeriesAccessModel>({
    episodeAccesses: [],
    seriesId: seriesHash,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);

  const isAllLoaded = allLoaded || start >= end;

  const loadNext = () => {
    if (isLoading || allLoaded) {
      return;
    }
    setIsLoading(true);

    //ロードする数
    const defaultLoadCount = 20;
    const loadCount = Math.min(defaultLoadCount, end - start - loadedEpisodes.length);
    //ソート順によってロード位置調整
    const startEp =
      sort === "old" ? start + loadedEpisodes.length : end - loadedEpisodes.length - loadCount;

    const epPromise = loadEps(seriesHash, startEp, loadCount);
    const accessPromise = loadAccess(seriesHash, startEp, loadCount);

    Promise.all([epPromise, accessPromise]).then((values) => {
      //EPs
      const eps = values[0];
      const newEps = loadedEpisodes.concat(
        sort === "old" ? eps.series.episodes : eps.series.episodes.reverse(),
      );
      setLoadedEpisodes(newEps);

      //Access
      const access = values[1];
      if (access.seriesAccess.episodeAccesses?.length) {
        const newAccess: SeriesAccessModel = {
          seriesId: seriesHash,
          episodeAccesses: loadedAccess.episodeAccesses?.concat(
            access.seriesAccess.episodeAccesses,
          ),
        };
        setLoadedAccess(newAccess);
      }
      setIsLoading(false);
      if (start + newEps.length >= end) {
        setAllLoaded(true);
      }
    });
  };

  const episodeAccessSet = seriesAccessToepisodeAccessSet(loadedAccess);

  return (
    <div className="series-eplist-loader">
      {loadedEpisodes.map((episode, index) => {
        return (
          <EpisodeListItem
            episode={episode}
            waitAndFreeWaiting={getWaitAndFreeWaiting(seriesWaitOn)}
            key={episode.id}
            access={episodeAccessSet.getEpisodeAccessByEpisodeId(episode.id)}
            currentEpisodeId={currentEpisodeId}
            displayIndex={start + index}
            displayedOn={displayedOn}
          />
        );
      })}
      {!isAllLoaded && (
        <button
          onClick={loadNext}
          disabled={isLoading}
          className={"series-eplist-loader-btn" + (isLoading ? " mode-loading" : "")}
        >
          <span className="series-eplist-loader-label">
            もっと見る
            <MdKeyboardArrowDown size={24} className="series-eplist-loader-icon" />
          </span>
        </button>
      )}
    </div>
  );
}
